import type { Instrument } from "@trader/shared/MarketData"
import { type KeyboardEvent, useEffect, useRef, useState } from "react"
import { call } from "../../api.ts"
import { setInstrument } from "../workspace/workspaceStore.ts"
import { rememberInstrument } from "./useInstrument.ts"
import { isTypingIn } from "../../ui/typing.ts"

const DEBOUNCE_MS = 120

/**
 * Symbol search for the active chart. Start typing anywhere on the page to
 * search (like TradingView); arrows pick, Enter opens, Escape closes. With
 * `onChoose` it picks a symbol for something else (the watchlist's Add).
 */
export function SymbolSearch({ autoFocus = false, label = "Search symbols", onCancel, onChoose, placeholder = "Symbol (e.g. RELIANCE, NIFTY50)" }: {
  readonly onChoose?: (instrument: Instrument) => void
  readonly onCancel?: () => void
  readonly label?: string
  readonly placeholder?: string
  /** Focused at once; and typing elsewhere on the page doesn't reach it (only the toolbar's does). */
  readonly autoFocus?: boolean
} = {}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<ReadonlyArray<Instrument>>([])
  const [highlighted, setHighlighted] = useState(0)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (onChoose !== undefined) return
    const onKey = (event: globalThis.KeyboardEvent) => {
      const typing = isTypingIn(document.activeElement)
      // A dialog (order ticket, settings) has the keyboard: don't type behind it.
      const dialogOpen = document.querySelector('[aria-modal="true"]') !== null
      if (typing || dialogOpen || event.ctrlKey || event.metaKey || event.altKey || !/^[a-z0-9]$/i.test(event.key)) return
      inputRef.current?.focus()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])

  useEffect(() => {
    const q = query.trim()
    if (q === "") {
      setResults([])
      return
    }
    let current = true
    const timer = setTimeout(() => {
      call((client) => client.instruments.search({ query: { q, limit: 12 } })).then(
        (found) => {
          if (!current) return
          setResults(found)
          setHighlighted(0)
        },
        () => current && setResults([])
      )
    }, DEBOUNCE_MS)
    return () => {
      current = false
      clearTimeout(timer)
    }
  }, [query])

  const choose = (instrument: Instrument) => {
    rememberInstrument(instrument)
    if (onChoose !== undefined) onChoose(instrument)
    else setInstrument(instrument.id)
    setQuery("")
    setOpen(false)
    inputRef.current?.blur()
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") setHighlighted((i) => Math.min(i + 1, results.length - 1))
    else if (event.key === "ArrowUp") setHighlighted((i) => Math.max(i - 1, 0))
    else if (event.key === "Enter" && results[highlighted]) choose(results[highlighted])
    else if (event.key === "Escape") {
      setQuery("")
      inputRef.current?.blur()
      onCancel?.()
    } else return
    event.preventDefault()
  }

  return (
    <div className="symbol-search">
      <input
        ref={inputRef}
        type="search"
        placeholder={placeholder}
        aria-label={label}
        autoFocus={autoFocus}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKeyDown}
      />
      {open && results.length > 0 && (
        <ul className="search-results" role="listbox">
          {results.map((instrument, i) => (
            <li
              key={instrument.id}
              role="option"
              aria-selected={i === highlighted}
              onPointerDown={(event) => {
                event.preventDefault()
                choose(instrument)
              }}
              onPointerEnter={() => setHighlighted(i)}
            >
              <span className="result-symbol">{instrument.symbol}</span>
              <span className="result-name">{instrument.name}</span>
              <span className="result-venue">{instrument.venue}{instrument.kind === "index" ? " · index" : ""}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
